import { Responsive, Scale, Shape, Text as TextModule } from '@visx/visx';
import { select } from 'd3';
import { debounce, groupBy } from 'lodash';
import { Dispatch, MouseEvent, SetStateAction, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import {
  defaultOpacity,
  defaultVoiceColor,
  fullOpacity,
  lowerOpacity,
  lowOpacity
} from './constants';
import MusicSheet from './models/MusicSheet';
import { areOccurrencesEqual, Occurrence, occurrenceKey, Pattern } from './models/Pattern';
import { Voice } from './models/Voice';
import PatternTooltip from './PatternTooltip';
import { SelectionContext } from './SelectionContext';
import { ThemeContext } from './ThemeContext';

// @visx/visx re-exports every visx package as a namespace.
const { ParentSize } = Responsive;
const { scaleLinear } = Scale;
const { Line } = Shape;
const { Text } = TextModule;

export const timelineID = 'timeline';

// The pointer has to rest this long on the timeline before the sheet turns to the page below it: moving across the
// timeline to the views below no longer flips pages (audit ISSUE-012), while resting on it does as in the paper.
const pageDwell = 250;

// Text of the occurrence marks: xx-small is about 9 px, i.e. about 5.5 px per character.
const characterWidth = 5.5;

/** The label as far as it fits on its mark (audit ISSUE-015: "C..." stood for CS1, CS2 and every other label). */
const fittingLabel = (label: string, width: number) => {
  const fitting = Math.floor((width - 2) / characterWidth);
  if (label.length <= Math.max(fitting, 1)) return label;
  // Without room for an ellipsis the first characters stay, e.g. A' for A'''' (not A, which is another pattern).
  return fitting <= 2 ? label.slice(0, Math.max(fitting, 1)) : `${label.slice(0, fitting - 1)}…`;
};

const toSVG = (svg: SVGSVGElement, position: { x: number; y: number }) => {
  const domPoint = new DOMPoint(position.x, position.y);
  const transformMatrix = svg.getScreenCTM();
  const translatedPoint = domPoint.matrixTransform(transformMatrix.inverse());

  return { x: translatedPoint.x, y: translatedPoint.y };
};

const Timeline = ({
  absoluteOffsetPageMap,
  hoveredOccurrenceIndex,
  hoveredOccurrences,
  hoveredPatternIDs,
  onPlayOccurrence,
  patterns,
  playingOccurrenceKey,
  selectedMusicSheet,
  setConfiguringID,
  setCurrentPage,
  setHoveredOccurrenceIndex,
  setHoveredPatternIDs,
  voices
}: {
  absoluteOffsetPageMap: Map<number, number>;
  hoveredOccurrenceIndex: number | undefined;
  hoveredOccurrences: Occurrence[];
  hoveredPatternIDs: string[];
  /** A double click on a mark plays its entry. */
  onPlayOccurrence: (occurrence: Occurrence) => void;
  patterns: Pattern[];
  /** occurrenceKey of the entry that is playing. */
  playingOccurrenceKey: string | undefined;
  selectedMusicSheet: MusicSheet;
  setConfiguringID: Dispatch<SetStateAction<string | undefined>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setHoveredOccurrenceIndex: Dispatch<SetStateAction<number | undefined>>;
  setHoveredPatternIDs: Dispatch<SetStateAction<string[]>>;
  voices: Voice[];
}) => {
  const { t: translate } = useTranslation();
  const pageTimer = useRef<number | undefined>(undefined);
  const leftPadding = 50;
  const padding = 25;
  // Below the page labels, every voice gets a band of the same height; its line runs through the middle.
  const topMargin = 14;
  const bottomMargin = 4;
  const voiceCount = voices.length;
  const voiceLineWidth = 1;

  return (
    // visx 4 renders the children absolutely positioned: without flexShrink the flex column would squeeze the timeline.
    <ParentSize className="mr-5" id={timelineID} parentSizeStyles={{ flexShrink: 0, height: 250 }}>
      {(parent) => {
        const lineWidth = parent.width - leftPadding - padding;
        const bandHeight = (parent.height - topMargin - bottomMargin) / Math.max(voiceCount, 1);
        const pageLabelSpacing = 48;
        let lastPageLabelX = -Infinity;
        const offsetsByPage = groupBy(
          Array.from(absoluteOffsetPageMap.entries()),
          ([, page]) => page
        );
        const offsetScale = scaleLinear({
          domain: [0, selectedMusicSheet?.lastOffset],
          range: [0, lineWidth],
          round: true
        });
        const svg = useRef<SVGSVGElement>(null);
        /** The page of the sheet that shows the music below the pointer. */
        const pageAt = (event: MouseEvent) => {
          const { x } = toSVG(svg.current, { x: event.clientX, y: event.clientY });
          if (x < leftPadding || x > leftPadding + lineWidth) return undefined;

          const offset = offsetScale.invert(x - leftPadding);
          let page = 1;
          for (const [pageOffset, pageOfOffset] of absoluteOffsetPageMap.entries()) {
            if (offset > pageOffset) page = pageOfOffset;
            else break;
          }

          return page;
        };

        if (selectedMusicSheet === undefined || absoluteOffsetPageMap.size === 0) {
          return (
            <div className="animate-pulse" style={{ height: 150 }}>
              <div className="w-full h-full mb-3 mt-3 rounded-md bg-gray-200"></div>
            </div>
          );
        } else {
          return (
            <svg
              height={parent.height}
              onClick={(event) => {
                const page = pageAt(event);
                window.clearTimeout(pageTimer.current);
                if (page !== undefined) setCurrentPage(page);
              }}
              onMouseLeave={() => window.clearTimeout(pageTimer.current)}
              onMouseMove={(event) => {
                const page = pageAt(event);
                window.clearTimeout(pageTimer.current);
                if (page !== undefined)
                  pageTimer.current = window.setTimeout(() => setCurrentPage(page), pageDwell);
              }}
              ref={svg}
              width={parent.width}
            >
              {Object.values(offsetsByPage).map((offsets, pageIndex) => {
                if (pageIndex === 0) return null;

                const firstPageOffset = offsets[0][0];
                const x = leftPadding + offsetScale(firstPageOffset);
                // Narrow timelines of long pieces label only every so many page breaks.
                const showLabel = x - lastPageLabelX >= pageLabelSpacing;
                if (showLabel) lastPageLabelX = x;

                return (
                  <g key={pageIndex}>
                    {showLabel && (
                      <text
                        fill="#5b6472"
                        style={{ fontSize: 10, textAnchor: 'middle' }}
                        x={x}
                        y={9}
                      >
                        {translate('timeline.page', { page: pageIndex + 1 })}
                      </text>
                    )}
                    <Line
                      from={{ x, y: 12 }}
                      key={pageIndex}
                      style={{
                        stroke: 'grey',
                        strokeDasharray: 2,
                        strokeOpacity: defaultOpacity,
                        strokeWidth: voiceLineWidth
                      }}
                      to={{ x, y: parent.height - 10 }}
                    />
                  </g>
                );
              })}
              {voices.map((voice, index) => (
                <VoiceLine
                  hoveredOccurrenceIndex={hoveredOccurrenceIndex}
                  hoveredOccurrences={hoveredOccurrences}
                  hoveredPatternIDs={hoveredPatternIDs}
                  bandHeight={bandHeight}
                  key={voice.number}
                  lastOffset={selectedMusicSheet?.lastOffset}
                  leftPadding={leftPadding}
                  lineWidth={lineWidth}
                  parentWidth={parent.width}
                  patterns={patterns.filter((pattern) =>
                    pattern.occurrences.some(
                      (occurrence) => occurrence.voiceNumber === voice.number
                    )
                  )}
                  onPlayOccurrence={onPlayOccurrence}
                  playingOccurrenceKey={playingOccurrenceKey}
                  setConfiguringID={setConfiguringID}
                  setHoveredOccurrenceIndex={setHoveredOccurrenceIndex}
                  setHoveredPatternIDs={setHoveredPatternIDs}
                  voice={voice}
                  voiceLineWidth={voiceLineWidth}
                  y={topMargin + bandHeight * (index + 0.5)}
                />
              ))}
            </svg>
          );
        }
      }}
    </ParentSize>
  );
};

const VoiceLine = ({
  bandHeight,
  hoveredOccurrenceIndex,
  hoveredOccurrences,
  hoveredPatternIDs,
  lastOffset,
  leftPadding,
  lineWidth,
  onPlayOccurrence,
  parentWidth,
  patterns,
  playingOccurrenceKey,
  setConfiguringID,
  setHoveredOccurrenceIndex,
  setHoveredPatternIDs,
  voice,
  voiceLineWidth,
  y
}: {
  bandHeight: number;
  hoveredOccurrenceIndex: number | undefined;
  hoveredOccurrences: Occurrence[];
  hoveredPatternIDs: string[];
  lastOffset: number | undefined;
  leftPadding: number;
  lineWidth: number;
  onPlayOccurrence: (occurrence: Occurrence) => void;
  parentWidth: number;
  patterns: Pattern[];
  playingOccurrenceKey: string | undefined;
  setConfiguringID: Dispatch<SetStateAction<string | undefined>>;
  setHoveredOccurrenceIndex: Dispatch<SetStateAction<number | undefined>>;
  setHoveredPatternIDs: Dispatch<SetStateAction<string[]>>;
  voice: Voice;
  voiceLineWidth: number;
  y: number;
}) => {
  const { t: translate } = useTranslation();
  const [selection] = useContext(SelectionContext);
  const [theme] = useContext(ThemeContext);

  const color = theme.colorByVoice || theme.colorVoices ? voice.color : defaultVoiceColor;
  const label = translate('family.voice', { voice: voice.number });
  const offsetScale = scaleLinear({ domain: [0, lastOffset], range: [0, lineWidth], round: true });
  const textBackgroundCornerRadius = 2;
  const textHeight = 15;
  const textPadding = 5;
  const textWidth = 35;
  const minimumOccurrenceWidthInOffsets = 2.5;
  const widthOf = (occurrence: Occurrence) =>
    offsetScale(
      Math.max(occurrence.lastOffset - occurrence.firstOffset, minimumOccurrenceWidthInOffsets)
    );

  const visibleOccurrences = lastOffset
    ? patterns
        .filter(
          (pattern) =>
            pattern.isVisible &&
            (selection.focusingID === undefined || selection.focusingID === pattern.id)
        )
        .flatMap((pattern) =>
          pattern.occurrences
            .filter((occurrence) => occurrence.voiceNumber === voice.number)
            .map((occurrence, occurrenceIndex) => ({ occurrence, occurrenceIndex, pattern }))
        )
    : [];

  // Overlapping occurrences get lanes of their own (audit ISSUE-014: they were drawn on top of each other). Greedy
  // interval partitioning in pixels; the lanes share the voice's band and become thinner when there are many.
  const laneEnds: number[] = [];
  const laneOf = new Map<(typeof visibleOccurrences)[number], number>();
  [...visibleOccurrences]
    .sort((first, second) => first.occurrence.firstOffset - second.occurrence.firstOffset)
    .forEach((item) => {
      const start = offsetScale(item.occurrence.firstOffset);
      let lane = laneEnds.findIndex((end) => end < start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = start + widthOf(item.occurrence);
      laneOf.set(item, lane);
    });
  const laneGap = 2;
  const laneCount = Math.min(
    Math.max(laneEnds.length, 1),
    Math.max(1, Math.floor((bandHeight - 6) / 10))
  );
  const occurrenceHeight = Math.min(20, (bandHeight - 6) / laneCount - laneGap);
  const lanesTop = y - (laneCount * occurrenceHeight + (laneCount - 1) * laneGap) / 2;

  return (
    <g>
      <rect
        fill={color}
        fillOpacity={lowerOpacity}
        height={textHeight}
        rx={textBackgroundCornerRadius}
        ry={textBackgroundCornerRadius}
        width={textWidth + textPadding * 2}
        x={0}
        y={y - textHeight / 2}
      ></rect>
      <text
        fill={tinycolor.mostReadable(color, ['#000', '#808080', '#fff']).toHexString()}
        style={{ dominantBaseline: 'central', fontSize: 'xx-small' }}
        textLength={textWidth}
        x={textPadding}
        y={y}
      >
        {label}
      </text>
      <Line
        from={{ x: leftPadding, y }}
        key={voice.number}
        style={{ stroke: color, strokeOpacity: defaultOpacity, strokeWidth: voiceLineWidth }}
        to={{ x: parentWidth, y }}
      />
      {visibleOccurrences.map((item) => {
        const { occurrence, occurrenceIndex, pattern } = item;
        const globalOccurrenceIndex = pattern.occurrences.indexOf(occurrence);
        const debouncedOnMouseEnter = debounce(() => {
          setHoveredOccurrenceIndex(globalOccurrenceIndex);
          setHoveredPatternIDs([pattern.id]);
        }, 150);
        const durationInOffsets = occurrence.lastOffset - occurrence.firstOffset;
        const isPlaying = playingOccurrenceKey === occurrenceKey(occurrence);
        const occurrenceWidth = widthOf(occurrence);
        const occurrenceX = leftPadding + offsetScale(occurrence.firstOffset);
        const occurrenceY =
          lanesTop + ((laneOf.get(item) ?? 0) % laneCount) * (occurrenceHeight + laneGap);
        const shouldBeTransparent =
          (hoveredPatternIDs.length > 0 && !hoveredPatternIDs.includes(pattern.id)) ||
          (hoveredOccurrenceIndex !== undefined &&
            globalOccurrenceIndex !== hoveredOccurrenceIndex) ||
          (hoveredOccurrences.length > 0 &&
            !hoveredOccurrences.some((hoveredOccurrence) =>
              areOccurrencesEqual(occurrence, hoveredOccurrence)
            ));

        return (
          <PatternTooltip
            hint={translate('timeline.playHint')}
            key={`${pattern.id}-${occurrenceIndex}`}
            pattern={pattern}
            shouldDisplayDisclaimer={durationInOffsets < minimumOccurrenceWidthInOffsets}
          >
            <g
              onMouseEnter={(event) => {
                const group =
                  (event.target as SVGGElement).tagName === 'g'
                    ? (event.target as SVGGElement)
                    : (event.target as SVGGElement).parentElement;

                select(group).raise();
                debouncedOnMouseEnter();
              }}
              onMouseLeave={() => {
                setHoveredOccurrenceIndex(undefined);
                setHoveredPatternIDs([]);
                debouncedOnMouseEnter.cancel();
              }}
              style={{ fillOpacity: shouldBeTransparent ? lowOpacity : fullOpacity }}
            >
              <rect
                className="cursor-pointer"
                data-playing={isPlaying || undefined}
                fill={pattern.color}
                height={occurrenceHeight}
                onClick={() => setConfiguringID(pattern.id)}
                onDoubleClick={() => onPlayOccurrence(occurrence)}
                rx={2}
                ry={2}
                // The entry that sounds is outlined.
                stroke={isPlaying ? '#0b0b0b' : undefined}
                strokeWidth={isPlaying ? 2 : undefined}
                width={occurrenceWidth}
                x={occurrenceX}
                y={occurrenceY}
              ></rect>
              <Text
                // The whole mark configures its pattern, including its label (audit ISSUE-017).
                pointerEvents="none"
                fill={tinycolor
                  .mostReadable(pattern.color, ['#000', '#808080', '#fff'])
                  .toHexString()}
                fontSize="xx-small"
                textAnchor="middle"
                verticalAnchor="middle"
                x={occurrenceX + occurrenceWidth / 2}
                y={occurrenceY + occurrenceHeight / 2}
              >
                {fittingLabel(pattern.label ?? '', occurrenceWidth)}
              </Text>
            </g>
          </PatternTooltip>
        );
      })}
    </g>
  );
};

export default Timeline;
